import { useState, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import Tesseract from "tesseract.js";
import { Link } from "react-router-dom";

import Layout from "../Common/Layout";

/* ---------- PDF WORKER ---------- */
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

/* ---------- OCR CONFIG ---------- */
const OCR_CONFIG = {
  scale: 3.0,
  threshold: 170,
  pageSegMode: 3,
};

/* ---------- TEXT CLEANING ---------- */
function cleanOcrText(raw) {
  if (!raw) return "";
  return raw
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .split("\n")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .join("\n")
    .trim();
}

/* ---------- IMAGE PREPROCESSING ---------- */
function preprocessCanvasStrong(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const bw = gray < OCR_CONFIG.threshold ? 0 : 255;
    data[i] = data[i + 1] = data[i + 2] = bw;
  }

  ctx.putImageData(imageData, 0, 0);
}

/* ---------- FIELD EXTRACTION ---------- */
function extractSerialNo(text) {
  const patterns = [
    /Serial\s*No\.?\s*[:\-]?\s*([A-Za-z0-9\/\-]+)/i,
    /Sr\.?\s*No\.?\s*[:\-]?\s*([A-Za-z0-9\/\-]+)/i,
  ];

  for (const regex of patterns) {
    const match = text.match(regex);
    if (match) return match[1];
  }
  return "";
}

function extractVehicleNo(text) {
  const match = text.match(/Vehicle\s*No\.?\s*[:\-]?\s*([A-Za-z0-9\-]+)/i);
  return match ? match[1] : "";
}

/* ---------- MAIN COMPONENT ---------- */
export default function PdfOcrExtractor() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [showManualHint, setShowManualHint] = useState(false);

  const [formData, setFormData] = useState({
    serialNo: sessionStorage.getItem("serial_no") || "",
    vehicleNo: sessionStorage.getItem("truckNo") || "",
  });

  /* ---------- STORE SERIAL NO SAFELY ---------- */
  useEffect(() => {
    if (formData.serialNo?.trim()) {
      sessionStorage.setItem("serial_no", formData.serialNo.trim());
    }
  }, [formData.serialNo]);

  /* ---------- FILE HANDLER ---------- */
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError("");
    setText("");
    setShowManualHint(false);
    setProgress("Initializing OCR...");

    try {
      const buffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

      let fullText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        setProgress(`Processing page ${i} of ${pdf.numPages}`);

        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: OCR_CONFIG.scale });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: ctx, viewport }).promise;
        preprocessCanvasStrong(canvas);

        const result = await Tesseract.recognize(canvas, "eng");
        fullText += result.data.text + "\n\n";
      }

      const cleaned = cleanOcrText(fullText);
      setText(cleaned);

      const serialNo = extractSerialNo(cleaned);
      const vehicleNo = extractVehicleNo(cleaned);

      if (!serialNo) setShowManualHint(true);

      setFormData({
        serialNo: serialNo || "",
        vehicleNo: vehicleNo || formData.vehicleNo,
      });

      setProgress("Completed");
    } catch (err) {
      console.error(err);
      setError("Failed to process PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-sm border-0">
              <div
                className="card-body p-4"
                style={{
                  backgroundColor: "#0F71AD",
                  color: "white",
                  borderRadius: "10px",
                }}
              >
                <h4 className="text-center mb-4">
                  PDF OCR – Serial & Vehicle Extractor
                </h4>

                {/* Upload */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Upload Scanned PDF
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="application/pdf"
                    onChange={handleFileChange}
                  />
                </div>

                {loading && (
                  <div className="alert alert-info py-2">{progress}</div>
                )}

                {error && (
                  <div className="alert alert-danger py-2">{error}</div>
                )}

                {showManualHint && (
                  <div className="alert alert-warning">
                    Pdf is not clear Please enter it manually.
                  </div>
                )}

                {/* Manual / Extracted Fields */}
                <hr />

                <Link
                  to="/kata-slip-data" // 👈 change route
                  className="btn"
                  style={{
                    backgroundColor: "#EC662C",
                    color: "white",
                  }}
                >
                  Manual Entry
                </Link>

                {/* OCR Text */}
                {text && (
                  <>
                    <hr />
                    <label className="form-label fw-semibold">
                      OCR Raw Text
                    </label>
                    <textarea
                      className="form-control"
                      rows="6"
                      value={text}
                      readOnly
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
